import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { CSSTransition } from "react-transition-group";
import {
  CalendarPlus,
  Check,
  Loader2,
  X,
  AlertCircle,
  Clock,
  Phone,
  MapPin,
  Calendar,
  AlertTriangle,
  Bug,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../../utils/api";
import { formatDateTime as formatLocaleDateTime } from "../../utils/dateFormatter";
import SlotPicker from "./SlotPicker";
import { useToast } from "../common/Toast";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

// Dev mode flag - shows all appointment states for testing
const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

// Feature release date - windows that closed before this date are hidden
// (users never had a chance to book since the feature didn't exist)
const FEATURE_RELEASE_DATE = new Date('2026-01-21');

// Minimum time to show loading state to prevent flickering
const MIN_LOADING_TIME = 500;

// Fade out duration in ms (should match CSS animation)
const FADE_OUT_DURATION = 500;

// Hook to ensure loading state is visible for minimum time
function useStableLoading(isLoading) {
  const [stableLoading, setStableLoading] = useState(isLoading);
  const loadingStartTime = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      loadingStartTime.current = Date.now();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStableLoading(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (loadingStartTime.current) {
      const elapsed = Date.now() - loadingStartTime.current;
      const remaining = MIN_LOADING_TIME - elapsed;

      if (remaining > 0) {
        timeoutRef.current = setTimeout(() => {
          setStableLoading(false);
          loadingStartTime.current = null;
        }, remaining);
      } else {
        setStableLoading(false);
        loadingStartTime.current = null;
      }
    } else {
      setStableLoading(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading]);

  return stableLoading;
}

const MEETING_TYPE_ICONS = {
  phone: Phone,
  in_person: MapPin,
};

const MEETING_TYPE_LABELS = {
  phone: "Phone Call",
  in_person: "In Person",
};

const MEETING_TYPE_INFO = {
  phone: "The office will call you at the scheduled time.",
  in_person: "65 Challenger Rd Suite 201, Ridgefield Park, NJ 07660",
};

// Reusable card styles
const CARD_STYLES = {
  canBook: "bg-gradient-to-r from-[#589DD5]/10 to-[#589DD5]/5 dark:from-transparent dark:to-transparent border border-[#589DD5]/20 dark:border-[#589DD5]/30 rounded-xl p-3 sm:p-5",
  scheduled: "bg-white dark:bg-transparent border border-emerald-300 dark:border-emerald-500 rounded-xl p-3 sm:p-5",
  completed: "bg-gradient-to-r from-emerald-50 to-emerald-100/50 dark:from-transparent dark:to-transparent border border-emerald-200 dark:border-emerald-500 rounded-xl p-3 sm:p-5",
  gray: "bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-5",
};

// Booking Modal Content
function BookingModalContent({
  onClose,
  studentName,
  courseName,
  slots,
  selectedSlot,
  onSelectSlot,
  selectedMeetingType,
  onSelectMeetingType,
  error,
  isBooking,
  onBook,
}) {
  const { t } = useTranslation();
  const [mobileStep, setMobileStep] = useState(1); // 1 = instructions, 2 = slot picker

  return (
    <div className="flex items-center justify-center p-4 h-full">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={() => !isBooking && onClose()}
      />
      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/50 max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
        <button
          onClick={onClose}
          disabled={isBooking}
          className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#589DD5]/20 flex items-center justify-center">
            <CalendarPlus className="w-5 h-5 text-[#589DD5]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('appointment.booking.title')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{studentName} · {courseName}</p>
          </div>
        </div>

        {/* Mobile Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-4 sm:hidden">
          <div className={`w-2 h-2 rounded-full transition-colors ${mobileStep === 1 ? 'bg-[#589DD5]' : 'bg-gray-300 dark:bg-gray-600'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${mobileStep === 2 ? 'bg-[#589DD5]' : 'bg-gray-300 dark:bg-gray-600'}`} />
        </div>

        {/* Desktop: Show all content. Mobile: Step 1 = Instructions only */}
        <div className={`${mobileStep === 2 ? 'hidden sm:block' : ''}`}>
          {/* Important Notes */}
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('appointment.booking.pleaseNote')}</p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
              <li>{t('appointment.booking.note1')}</li>
              <li>{t('appointment.booking.note2')}</li>
              <li>{t('appointment.booking.note3')}</li>
              <li>{t('appointment.booking.note4')}</li>
              <li>{t('appointment.booking.note5')}</li>
            </ul>
          </div>
        </div>

        {/* Desktop: Show all content. Mobile: Step 2 = Slot Picker only */}
        <div className={`${mobileStep === 1 ? 'hidden sm:block' : ''}`}>
          {/* Slot Picker with integrated meeting type selector */}
          <div className="mb-4">
            <SlotPicker
              slots={slots}
              onSelect={onSelectSlot}
              selectedSlot={selectedSlot}
              selectedMeetingType={selectedMeetingType}
              onSelectMeetingType={onSelectMeetingType}
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-transparent border border-red-200 dark:border-red-500 rounded-lg p-3 mb-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

        {/* Mobile Step 1: Cancel + Next buttons */}
        <div className={`flex gap-3 sm:hidden ${mobileStep === 2 ? 'hidden' : ''}`}>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={() => setMobileStep(2)}
            className="flex-1 px-4 py-2 bg-[#589DD5] text-white text-sm font-medium rounded-lg hover:bg-[#4a8bc4] transition-colors duration-200"
          >
            {t('appointment.booking.next')}
          </button>
        </div>

        {/* Mobile Step 2: Back + Confirm buttons */}
        <div className={`flex gap-3 sm:hidden ${mobileStep === 1 ? 'hidden' : ''}`}>
          <button
            onClick={() => setMobileStep(1)}
            disabled={isBooking}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
          >
            {t('appointment.booking.back')}
          </button>
          <button
            onClick={onBook}
            disabled={isBooking || !selectedSlot || !selectedMeetingType}
            className="flex-1 px-4 py-2 bg-[#589DD5] text-white text-sm font-medium rounded-lg hover:bg-[#4a8bc4] transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isBooking ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('appointment.booking.booking')}
              </>
            ) : (
              t('appointment.booking.confirmBooking')
            )}
          </button>
        </div>

        {/* Desktop: Cancel + Confirm buttons */}
        <div className="hidden sm:flex gap-3">
          <button
            onClick={onClose}
            disabled={isBooking}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={onBook}
            disabled={isBooking || !selectedSlot || !selectedMeetingType}
            className="flex-1 px-4 py-2 bg-[#589DD5] text-white text-sm font-medium rounded-lg hover:bg-[#4a8bc4] transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isBooking ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('appointment.booking.booking')}
              </>
            ) : (
              t('appointment.booking.confirmBooking')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable Booking Modal Component with animation
function BookingModal({
  isOpen,
  onClose,
  studentName,
  courseName,
  slots,
  selectedSlot,
  onSelectSlot,
  selectedMeetingType,
  onSelectMeetingType,
  error,
  isBooking,
  onBook,
}) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex: 9999 }}>
        <BookingModalContent
          onClose={onClose}
          studentName={studentName}
          courseName={courseName}
          slots={slots}
          selectedSlot={selectedSlot}
          onSelectSlot={onSelectSlot}
          selectedMeetingType={selectedMeetingType}
          onSelectMeetingType={onSelectMeetingType}
          error={error}
          isBooking={isBooking}
          onBook={onBook}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}

// Cancel Modal Content
function CancelModalContent({
  onClose,
  isEarly,
  courseName,
  isCanceling,
  onCancel,
}) {
  const { t } = useTranslation();

  const ordinal = isEarly ? t('appointment.ordinal.first') : t('appointment.ordinal.second');

  return (
    <div className="flex items-center justify-center p-4 h-full">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={() => !isCanceling && onClose()}
      />
      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/50 max-w-md w-full max-h-[80vh] overflow-y-auto p-6">
        <button
          onClick={onClose}
          disabled={isCanceling}
          className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-transparent dark:border dark:border-red-500 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('appointment.cancel.title')}
          </h3>
        </div>

        <div className="bg-amber-50 dark:bg-transparent border border-amber-200 dark:border-amber-500 rounded-lg p-3 mb-4">
          <p className="text-amber-800 dark:text-amber-400 text-sm font-medium">
            {t('appointment.cancel.warning')}
          </p>
          <p className="text-amber-700 dark:text-amber-400/80 text-xs mt-1">
            {t('appointment.cancel.warningDesc')}
          </p>
        </div>

        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          {t('appointment.cancel.confirmMessage', { ordinal })}{" "}
          <strong className="dark:text-white">{courseName}</strong>?
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isCanceling}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
          >
            {t('appointment.cancel.keepAppointment')}
          </button>
          <button
            onClick={onCancel}
            disabled={isCanceling}
            className="flex-1 px-4 py-2 border border-red-300 dark:border-red-500 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isCanceling ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('appointment.cancel.canceling')}
              </>
            ) : (
              t('appointment.cancel.cancelAppointment')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable Cancel Modal Component with animation
function CancelModal({
  isOpen,
  onClose,
  isEarly,
  courseName,
  isCanceling,
  onCancel,
}) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex: 9999 }}>
        <CancelModalContent
          onClose={onClose}
          isEarly={isEarly}
          courseName={courseName}
          isCanceling={isCanceling}
          onCancel={onCancel}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}

// Dev mode component to show all appointment states
function DevModePreview({ type, onOpenBookingModal, onOpenCancelModal, toast }) {
  const { t, i18n } = useTranslation();
  const isEarly = type === "early";
  const mockDateTime = i18n.language === 'ko' ? "2025년 1월 20일 월요일 오후 2:00" : "Monday, January 20, 2025, 2:00 PM";
  const mockDate = i18n.language === 'ko' ? "2025년 1월 15일 오후 11:00 EST" : "January 15, 2025 at 11:00 PM EST";

  return (
    <div className="border-2 border-dashed border-purple-400 dark:border-purple-600 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <Bug className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
          {t('devMode.appointmentTitle')} ({t(`appointment.schedule.${isEarly ? 'first' : 'second'}`)})
        </span>
      </div>

      {/* State 1: Loading */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          1. {t('devMode.loadingState')}
        </p>
        <div className={CARD_STYLES.canBook}>
          <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">{t('actions.loading')}</span>
          </div>
        </div>
      </div>

      {/* State 2: Window Not Started */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          2. {t('devMode.windowNotStarted')}
        </p>
        <div className={CARD_STYLES.gray}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                {t(`appointment.schedule.${isEarly ? 'first' : 'second'}`)} {t('appointment.schedule.consultationAppointment')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('appointment.schedule.bookingOpensOn', { date: mockDate })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* State 3: Can Book */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          3. {t('devMode.canBook')}
        </p>
        <div className={CARD_STYLES.canBook}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#589DD5]/20 flex items-center justify-center">
                <CalendarPlus className="w-5 h-5 sm:w-6 sm:h-6 text-[#589DD5]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                  {t(`appointment.schedule.${isEarly ? 'scheduleFirstFull' : 'scheduleSecondFull'}`)}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  {t(`appointment.schedule.${isEarly ? 'descFirstFull' : 'descSecondFull'}`)}
                </p>
                <p className="text-xs text-[#589DD5] mt-1">{t('appointment.schedule.daysLeftToBook', { count: 14 })}</p>
              </div>
            </div>
            <button
              onClick={onOpenBookingModal}
              className="flex-shrink-0 w-full sm:w-auto px-4 py-2 bg-[#589DD5] text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-[#4a8bc4] transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              {t('appointment.schedule.selectTime')}
            </button>
          </div>
        </div>
      </div>

      {/* State 4: Appointment Scheduled */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          4. {t('devMode.appointmentScheduled')}
        </p>
        <div className={CARD_STYLES.scheduled}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-100 dark:bg-transparent dark:border dark:border-emerald-500 flex items-center justify-center">
                <Check className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                  {t('appointment.status.scheduled')}
                </h3>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mt-1">
                  {mockDateTime} (ET)
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Phone className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('appointment.meetingType.phone')}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('appointment.meetingInfo.phone')}
                </p>
              </div>
            </div>
            <button
              onClick={onOpenCancelModal}
              className="flex-shrink-0 w-full sm:w-auto px-4 py-2 border border-red-300 dark:border-red-500 text-red-600 dark:text-red-400 text-xs sm:text-sm font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors duration-200"
            >
              {t('appointment.cancel.cancelAppointment')}
            </button>
          </div>
        </div>
      </div>

      {/* State 5: Appointment Completed */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          5. {t('devMode.appointmentCompleted')}
        </p>
        <div className={CARD_STYLES.completed}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-200 dark:bg-transparent dark:border dark:border-emerald-500 flex items-center justify-center">
              <Check className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-800 dark:text-emerald-400 text-sm sm:text-base">
                {t('appointment.status.completed')}
              </h3>
              <p className="text-xs sm:text-sm text-emerald-600 dark:text-emerald-400/80 mt-0.5">
                {mockDateTime} (ET)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* State 6: Appointment Canceled (by parent) */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          6. {t('devMode.canceledByParent')}
        </p>
        <div className={CARD_STYLES.gray}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                {t('appointment.status.canceled')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('appointment.status.canceledDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* State 7: Window Closed */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          7. {t('devMode.windowClosed')}
        </p>
        <div className={CARD_STYLES.gray}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                {t('appointment.status.windowClosed')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('appointment.status.contactOffice')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
          8. {t('devMode.toastNotifications')}
        </p>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-5">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => toast.success(t('appointment.toast.bookedMessage', { course: 'Test Course' }), {
                title: t('appointment.toast.bookedTitle'),
                duration: 6000,
              })}
              className="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors"
            >
              {t('devMode.successToast')}
            </button>
            <button
              onClick={() => toast.warning(t('appointment.toast.canceledMessage', { ordinal: t('appointment.ordinal.first'), course: 'Test Course' }), {
                title: t('appointment.toast.canceledTitle'),
                duration: 6000,
              })}
              className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors"
            >
              {t('devMode.warningToast')}
            </button>
            <button
              onClick={() => toast.error(t('appointment.toast.bookingFailed'))}
              className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
            >
              {t('devMode.errorToast')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppointmentScheduler({
  type,
  studentId,
  studentName,
  courseId,
  courseName,
}) {
  const { t } = useTranslation();
  const isEarly = type === "early";
  const toast = useToast();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const stableLoading = useStableLoading(isLoading);

  // Track if initial load is complete (for fade-in animation)
  const [hasLoaded, setHasLoaded] = useState(false);

  // Track visibility for fade-out animation before hiding
  const [isVisible, setIsVisible] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const [eligibility, setEligibility] = useState(null);
  const [slots, setSlots] = useState([]);
  const [existingAppointment, setExistingAppointment] = useState(null);
  const [error, setError] = useState(null);

  // Booking flow state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedMeetingType, setSelectedMeetingType] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  // Cancellation flow state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  // Server-clock offset (ms): serverTime - clientTime, derived from the API Date
  // header. Auto-unlock timing runs on this instead of the (possibly wrong) device clock.
  const serverSkewRef = useRef(0);

  // Fetch eligibility and existing appointments
  // `silent` skips the loading/error UI — used for background revalidation
  // (auto-unlock timer, tab refocus) so the card doesn't flash a spinner.
  const fetchData = useCallback(async (silent = false) => {
    if (!studentId || !courseId) {
      setIsLoading(false);
      return;
    }

    if (!silent) {
      setIsLoading(true);
      setError(null);
    }

    try {
      // Check eligibility
      const eligibilityRes = await api.get(
        `/parent/children/${studentId}/courses/${courseId}/appointment-eligibility?type=${type}`
      );

      // Derive server-clock offset so auto-unlock timing follows the server's clock,
      // not a misconfigured device clock. Prefer the body's server_time (always
      // readable); fall back to the Date header (stripped on cross-origin responses).
      const serverTimeStr = eligibilityRes?.data?.server_time || eligibilityRes?.headers?.date;
      if (serverTimeStr) {
        const serverMs = Date.parse(serverTimeStr);
        if (!Number.isNaN(serverMs)) {
          serverSkewRef.current = serverMs - Date.now();
        }
      }

      setEligibility(eligibilityRes.data);

      // Get existing appointments
      const appointmentsRes = await api.get(
        `/parent/children/${studentId}/courses/${courseId}/appointments`
      );
      const appointments = appointmentsRes.data.appointments || [];
      // Find the most relevant appointment for this type
      // Priority: BOOKED (0) > COMPLETED (1) > CANCELED_PARENT (2) > CANCELED_ADMIN (3)
      const typeAppointments = appointments.filter((a) => a.appointment_type === type);
      const existing = typeAppointments.find((a) => a.status === 0) // BOOKED
        || typeAppointments.find((a) => a.status === 1) // COMPLETED
        || typeAppointments.find((a) => a.status === 2) // CANCELED_PARENT
        || typeAppointments.find((a) => a.status === 3); // CANCELED_ADMIN
      setExistingAppointment(existing || null);

      // If eligible, fetch available slots
      if (eligibilityRes.data[type]?.can_book) {
        const slotsRes = await api.get(
          `/parent/children/${studentId}/courses/${courseId}/available-slots?type=${type}`
        );
        setSlots(slotsRes.data.slots || []);
      }

      // Background revalidation succeeded — clear any stale error from a prior load/booking.
      if (silent) setError(null);
    } catch (err) {
      console.error("Failed to fetch appointment data:", err);
      // Don't clobber the current view with an error on a background refresh.
      if (!silent) setError("Failed to load appointment information.");
    } finally {
      if (!silent) setIsLoading(false);
      setHasLoaded(true);
    }
  }, [studentId, courseId, type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Determine if component should be hidden (course too short, etc.)
  const typeData = eligibility?.[type];
  const reason = typeData?.reason;
  const shouldHide = !stableLoading && hasLoaded && (!typeData || reason === "course_too_short" || reason === "late_not_available");

  // Handle fade-out when component should disappear
  useEffect(() => {
    if (shouldHide && isVisible) {
      // Start fade-out animation (CSS handles the transition)
      setIsFadingOut(true);
      setIsVisible(false);

      // After fade completes, mark as no longer fading (for height collapse)
      const timer = setTimeout(() => {
        setIsFadingOut(false);
      }, FADE_OUT_DURATION);

      return () => clearTimeout(timer);
    } else if (!shouldHide && !isVisible) {
      // Component should show again - reset states
      setIsVisible(true);
      setIsFadingOut(false);
    }
  }, [shouldHide, isVisible]);

  // Auto-unlock: while the window hasn't opened yet, refetch from the server exactly
  // at the boundary (using server-corrected time) and whenever the tab is refocused.
  // The server stays the authority — we only re-ask, never unlock locally.
  const pendingWindowStart =
    typeData?.reason === "window_not_started" && typeData?.window?.start
      ? Date.parse(typeData.window.start)
      : null;

  useEffect(() => {
    if (pendingWindowStart === null || Number.isNaN(pendingWindowStart)) return;

    // Server-corrected time. A `window_not_started` reason always means the server's
    // clock is before the boundary, so this is reliably in the future in real usage.
    const ms = pendingWindowStart - (Date.now() + serverSkewRef.current);

    let timer;
    if (ms > 0) {
      // Fire just AFTER the boundary (+1s) so the server has definitely crossed it,
      // then revalidate silently — the server (authority) decides; we never unlock locally.
      // setTimeout caps near 2^31 ms (~24.8 days).
      timer = setTimeout(() => fetchData(true), Math.min(ms + 1000, 2 ** 31 - 1));
    }

    // Also re-check whenever the tab is refocused (covers backgrounded/asleep tabs
    // where the timer was throttled). Silent so the card doesn't flash a spinner.
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchData(true);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pendingWindowStart, fetchData]);

  // Handle slot selection - auto-select meeting type if only one available
  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    if (!slot) {
      setSelectedMeetingType(null);
      return;
    }
    const meetingTypes = slot.meeting_types || [slot.meeting_type];
    if (meetingTypes.length === 1) {
      setSelectedMeetingType(meetingTypes[0]);
    } else {
      setSelectedMeetingType(null); // Reset for multi-type slots
    }
  };

  // Handle booking
  const handleBook = async () => {
    if (!selectedSlot) return;

    // Determine meeting type to use
    const meetingTypes = selectedSlot.meeting_types || [selectedSlot.meeting_type];
    const meetingTypeToUse = meetingTypes.length === 1 ? meetingTypes[0] : selectedMeetingType;

    if (!meetingTypeToUse) return;

    setIsBooking(true);
    setError(null);

    try {
      await api.post(
        `/parent/children/${studentId}/courses/${courseId}/appointments`,
        {
          type,
          availability_id: selectedSlot.availability_id,
          slot_datetime: selectedSlot.datetime_iso,
          meeting_type: meetingTypeToUse,
        }
      );

      setShowBookingModal(false);
      setSelectedSlot(null);
      toast.success(t('appointment.toast.bookedMessage', { course: courseName }), {
        title: t('appointment.toast.bookedTitle'),
        duration: 6000,
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to book appointment:", err);
      const errorMessage = err.response?.data?.error ||
        "Failed to book appointment. The slot may no longer be available.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsBooking(false);
    }
  };

  // Handle cancellation
  const handleCancel = async () => {
    if (!existingAppointment) return;

    setIsCanceling(true);
    setError(null);

    try {
      await api.delete(
        `/parent/children/${studentId}/courses/${courseId}/appointments/${existingAppointment.id}`
      );

      setShowCancelModal(false);
      const ordinal = isEarly ? t('appointment.ordinal.first') : t('appointment.ordinal.second');
      toast.warning(t('appointment.toast.canceledMessage', { ordinal, course: courseName }), {
        title: t('appointment.toast.canceledTitle'),
        duration: 6000,
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to cancel appointment:", err);
      const errorMessage = err.response?.data?.error || "Failed to cancel appointment.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsCanceling(false);
    }
  };

  // Scheduled appointment time, pinned to Eastern (office timezone). Shares the
  // same locale-aware formatter as formatOpensAt.
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "";
    return formatLocaleDateTime(date, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    });
  };

  // Format the exact booking-open instant in the parent's LOCAL timezone
  // (e.g. "June 15, 2026 at 11:00 PM"). window.start is an exact UTC instant from the
  // server, so the displayed local time stays in lockstep with the server's open check.
  const formatOpensAt = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "";
    // Shared locale-aware formatter; no `timeZone` → render in the parent's own zone,
    // and no zone label — the time shown is already their wall-clock time.
    return formatLocaleDateTime(date, {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get translated meeting type label
  const getMeetingTypeLabel = (meetingType) => {
    const typeMap = { phone: 'phone', in_person: 'inPerson' };
    return t(`appointment.meetingType.${typeMap[meetingType] || 'phone'}`);
  };

  // Get translated meeting type info
  const getMeetingTypeInfo = (meetingType) => {
    const typeMap = { phone: 'phone', in_person: 'inPerson' };
    return t(`appointment.meetingInfo.${typeMap[meetingType] || 'phone'}`);
  };

  // Dev mode - show all states for testing
  if (DEV_MODE) {
    return (
      <>
        <DevModePreview
          type={type}
          onOpenBookingModal={() => setShowBookingModal(true)}
          onOpenCancelModal={() => setShowCancelModal(true)}
          toast={toast}
        />

        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          studentName={studentName}
          courseName={courseName}
          slots={slots}
          selectedSlot={selectedSlot}
          onSelectSlot={handleSelectSlot}
          selectedMeetingType={selectedMeetingType}
          onSelectMeetingType={setSelectedMeetingType}
          error={error}
          isBooking={isBooking}
          onBook={handleBook}
        />

        <CancelModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          isEarly={isEarly}
          courseName={courseName}
          isCanceling={isCanceling}
          onCancel={handleCancel}
        />
      </>
    );
  }

  // Loading state (use stable loading to prevent flickering)
  if (stableLoading) {
    return (
      <div className={`${CARD_STYLES.canBook} mb-3 sm:mb-4`}>
        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">{t('actions.loading')}</span>
        </div>
      </div>
    );
  }

  // Fade-in wrapper for content after loading, fade-out when hiding
  const contentClassName = hasLoaded
    ? isFadingOut
      ? "animate-fade-out"
      : "animate-fade-in"
    : "";

  // Determine what to show based on state
  // API returns { early: { can_book, reason, window }, late: { ... } }
  const canBook = typeData?.can_book;
  const window = typeData?.window;
  const hasExistingBooking =
    existingAppointment && existingAppointment.status === 0;
  const wasCanceled =
    existingAppointment && existingAppointment.status === 2;
  const wasCompleted =
    existingAppointment && existingAppointment.status === 1;
  const wasCanceledByAdmin =
    existingAppointment && existingAppointment.status === 3;

  // Not eligible (course too short, appointment type not available for this course)
  // Instead of returning null, render a collapsing container for smooth transition
  if (shouldHide) {
    return (
      <div
        className="overflow-hidden transition-all duration-500 ease-out"
        style={{
          opacity: isFadingOut ? 0 : 1,
          maxHeight: isFadingOut ? '200px' : '0px',
          marginBottom: isFadingOut ? undefined : '0px',
        }}
      />
    );
  }

  // USER ACTIONS TAKE PRIORITY OVER WINDOW STATUS
  // Check user actions first before checking window status

  // Was canceled by parent - cannot reschedule (even if window closed)
  if (wasCanceled) {
    return (
      <div className={`${CARD_STYLES.gray} mb-3 sm:mb-4 ${contentClassName}`}>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              {t('appointment.status.canceled')}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {t('appointment.status.canceledDesc')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Was completed (even if window closed)
  if (wasCompleted) {
    return (
      <div className={`${CARD_STYLES.completed} mb-3 sm:mb-4 ${contentClassName}`}>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-200 dark:bg-transparent dark:border dark:border-emerald-500 flex items-center justify-center">
            <Check className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-800 dark:text-emerald-400 text-sm sm:text-base">
              {t('appointment.status.completed')}
            </h3>
            <p className="text-xs sm:text-sm text-emerald-600 dark:text-emerald-400/80 mt-0.5">
              {formatDateTime(existingAppointment.scheduled_at)} (ET)
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Window not started yet
  if (reason === "window_not_started" && window) {
    return (
      <div className={`${CARD_STYLES.gray} mb-3 sm:mb-4 ${contentClassName}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                {isEarly ? t('appointment.schedule.first') : t('appointment.schedule.second')} {t('appointment.schedule.consultationAppointment')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('appointment.schedule.bookingOpensOn', { date: formatOpensAt(window.start) })}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Window closed (only show if no user action taken)
  if (reason === "window_closed") {
    // Hide completely if window closed before feature release (user never had a chance)
    const windowEnd = window?.end ? new Date(window.end) : null;
    if (windowEnd && windowEnd < FEATURE_RELEASE_DATE) {
      return null;
    }

    // Window closed after feature release - show "Window Closed" message
    return (
      <div className={`${CARD_STYLES.gray} mb-3 sm:mb-4 ${contentClassName}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                {t('appointment.status.windowClosed')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('appointment.status.contactOffice')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Already booked - show appointment details
  if (hasExistingBooking) {
    const MeetingIcon =
      MEETING_TYPE_ICONS[existingAppointment.meeting_type] || Phone;

    return (
      <div className={`${CARD_STYLES.scheduled} mb-3 sm:mb-4 ${contentClassName}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-100 dark:bg-transparent dark:border dark:border-emerald-500 flex items-center justify-center">
              <Check className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                {t('appointment.status.scheduled')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mt-1">
                {formatDateTime(existingAppointment.scheduled_at)} (ET)
              </p>
              <div className="flex items-center gap-2 mt-2">
                <MeetingIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {getMeetingTypeLabel(existingAppointment.meeting_type)}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getMeetingTypeInfo(existingAppointment.meeting_type)}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCancelModal(true)}
            className="flex-shrink-0 w-full sm:w-auto px-4 py-2 border border-red-300 dark:border-red-500 text-red-600 dark:text-red-400 text-xs sm:text-sm font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors duration-200"
          >
            {t('appointment.cancel.cancelAppointment')}
          </button>
        </div>

        <CancelModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          isEarly={isEarly}
          courseName={courseName}
          isCanceling={isCanceling}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  // Was canceled by admin - can rebook (this is handled by canBook being true)

  // Can book - show booking UI
  if (canBook || wasCanceledByAdmin) {
    return (
      <div className={`${CARD_STYLES.canBook} mb-3 sm:mb-4 ${contentClassName}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#589DD5]/20 flex items-center justify-center">
              <CalendarPlus className="w-5 h-5 sm:w-6 sm:h-6 text-[#589DD5]" />
            </div>
            <div className="flex-1 min-w-0 sm:hidden">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                {isEarly ? t('appointment.schedule.scheduleFirst') : t('appointment.schedule.scheduleSecond')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {isEarly
                  ? t('appointment.schedule.descFirst')
                  : t('appointment.schedule.descSecond')}
              </p>
              {window && (
                <p className="text-xs text-[#589DD5] mt-1">
                  {t('appointment.schedule.daysLeftToBook', { count: window.days_remaining })}
                </p>
              )}
            </div>
          </div>
          <div className="hidden sm:block flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {isEarly ? t('appointment.schedule.scheduleFirstFull') : t('appointment.schedule.scheduleSecondFull')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              {isEarly
                ? t('appointment.schedule.descFirstFull')
                : t('appointment.schedule.descSecondFull')}
            </p>
            {window && (
              <p className="text-xs text-[#589DD5] mt-1">
                {t('appointment.schedule.windowClosesIn', { count: window.days_remaining })}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowBookingModal(true)}
            className="flex-shrink-0 w-full sm:w-auto px-4 py-2 bg-[#589DD5] text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-[#4a8bc4] transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <Calendar className="w-4 h-4" />
            {t('appointment.schedule.selectTime')}
          </button>
        </div>
        {error && <p className="text-red-500 dark:text-red-400 text-xs mt-2">{error}</p>}

        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          studentName={studentName}
          courseName={courseName}
          slots={slots}
          selectedSlot={selectedSlot}
          onSelectSlot={handleSelectSlot}
          selectedMeetingType={selectedMeetingType}
          onSelectMeetingType={setSelectedMeetingType}
          error={error}
          isBooking={isBooking}
          onBook={handleBook}
        />
      </div>
    );
  }

  // Fallback - no appointment UI to show
  return null;
}
